import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRight,
  faBuilding,
  faCheck,
  faClockRotateLeft,
  faEnvelope,
  faLocationDot,
  faPhone,
  faShieldHalved,
  faUser,
  faUserTie,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import {
  toast,
} from "react-hot-toast";

import {
  ApiError,
} from "../api/apiError";

import {
  accountUpgradeRequestsApi,
} from "../api/accountUpgradeRequestsApi";

import type {
  AccountUpgradeRequest,
  UserAccountLog,
} from "../types/account-upgrade-requests.types";

import "../screenstyles/AccountUpgradeRequests.css";

const AGENT_LEVELS = [
  "AgentA",
  "AgentB",
  "AgentC",
  "AgentD",
  "AgentE",
  "AgentF",
  "AgentG",
  "AgentH",
];

const TECH_LEVELS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
];

function formatTimestamp(
  value: any,
): string {
  if (!value) {
    return "—";
  }

  let date:
    Date |
    null = null;

  if (
    typeof value ===
    "string"
  ) {
    date =
      new Date(value);
  } else {
    const seconds =
      value.seconds ??
      value._seconds;

    if (
      typeof seconds ===
      "number"
    ) {
      date =
        new Date(
          seconds *
            1000,
        );
    }
  }

  if (
    !date ||
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year:
        "numeric",

      month:
        "long",

      day:
        "2-digit",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(date);
}

function getStatusLabel(
  value?:
    string |
    null,
) {
  switch (
    value
  ) {
    case "pending":
      return "بانتظار الموافقة";

    case "approved":
      return "تمت الموافقة";

    case "rejected":
      return "مرفوض";

    case "cancelled":
      return "ملغي";

    default:
      return value ||
        "غير محدد";
  }
}

function getLogBefore(
  log:
    UserAccountLog,
) {
  return (
    log.before
      ?.accountType ??
    log.previousAccountType ??
    "—"
  );
}

function getLogAfter(
  log:
    UserAccountLog,
) {
  return (
    log.after
      ?.accountType ??
    log.newAccountType ??
    "—"
  );
}

function getLogLevelBefore(
  log:
    UserAccountLog,
) {
  return (
    log.before
      ?.AccountLevel ??
    log.previousAccountLevel ??
    "—"
  );
}

function getLogLevelAfter(
  log:
    UserAccountLog,
) {
  return (
    log.after
      ?.AccountLevel ??
    log.newAccountLevel ??
    "—"
  );
}

const AccountUpgradeRequestDetails:
React.FC = () => {
  const {
    requestId,
  } =
    useParams<{
      requestId:
        string;
    }>();

  const navigate =
    useNavigate();

  const [
    request,
    setRequest,
  ] =
    useState<
      AccountUpgradeRequest |
      null
    >(null);

  const [
    logs,
    setLogs,
  ] =
    useState<
      UserAccountLog[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    logsLoading,
    setLogsLoading,
  ] =
    useState(false);

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState<
      "approve" |
      "reject" |
      null
    >(null);

  const [
    accountLevel,
    setAccountLevel,
  ] =
    useState("");

  const [
    approveReason,
    setApproveReason,
  ] =
    useState("");

  const [
    rejectReason,
    setRejectReason,
  ] =
    useState("");

  const availableLevels =
    useMemo(
      () => {
        if (
          request
            ?.requestedAccountType ===
          "وكيل"
        ) {
          return AGENT_LEVELS;
        }

        if (
          request
            ?.requestedAccountType ===
          "منصب"
        ) {
          return TECH_LEVELS;
        }

        return [];
      },
      [
        request,
      ],
    );

  const loadLogs =
    useCallback(
      async (
        uid:
          string,
      ) => {
        try {
          setLogsLoading(
            true,
          );

          const result =
            await accountUpgradeRequestsApi
              .getUserLogs(
                uid,
                50,
              );

          setLogs(
            result.items ??
              [],
          );
        } catch (
          error
        ) {
          console.error(
            "Error loading account logs:",
            error,
          );

          /**
           * لا نمنع عرض الطلب
           * إذا لم يمتلك الأدمن
           * صلاحية logs.
           */
          setLogs([]);
        } finally {
          setLogsLoading(
            false,
          );
        }
      },
      [],
    );

  const loadRequest =
    useCallback(
      async () => {
        if (
          !requestId
        ) {
          return;
        }

        try {
          setLoading(
            true,
          );

          const result =
            await accountUpgradeRequestsApi
              .getById(
                requestId,
              );

          const loadedRequest =
            result.request ??
            result.application ??
            null;

          if (
            !loadedRequest
          ) {
            throw new Error(
              "Upgrade request response does not contain request data",
            );
          }

          setRequest(
            loadedRequest,
          );

          const requestedLevel =
            loadedRequest
              .requestedAccountLevel ??
            "";

          setAccountLevel(
            requestedLevel,
          );

          const uid =
            loadedRequest.userId ||
            loadedRequest.uid;

          if (uid) {
            await loadLogs(
              uid,
            );
          }
        } catch (
          error
        ) {
          console.error(
            "Error loading upgrade request:",
            error,
          );

          if (
            error instanceof
            ApiError
          ) {
            if (
              error.status ===
              404
            ) {
              toast.error(
                "لم يتم العثور على طلب الترقية",
              );
            } else if (
              error.status ===
              403
            ) {
              toast.error(
                "لا تمتلك صلاحية عرض الطلب",
              );
            } else {
              toast.error(
                error.message,
              );
            }
          } else {
            toast.error(
              "فشل تحميل بيانات الطلب",
            );
          }
        } finally {
          setLoading(
            false,
          );
        }
      },
      [
        requestId,
        loadLogs,
      ],
    );

  useEffect(() => {
    if (
      !requestId
    ) {
      navigate(
        "/account-upgrade-requests",
      );

      return;
    }

    loadRequest();
  }, [
    requestId,
    navigate,
    loadRequest,
  ]);

  const handleApprove =
    async () => {
      if (
        !requestId ||
        !request
      ) {
        return;
      }

      if (
        availableLevels.length >
          0 &&
        !accountLevel
      ) {
        toast.error(
          "يرجى اختيار مستوى الحساب",
        );

        return;
      }

      const confirmed =
        window.confirm(
          `هل تريد الموافقة على تحويل حساب "${
            request.fullName ||
            request.userId
          }" إلى "${
            request.requestedAccountType
          }"؟`,
        );

      if (
        !confirmed
      ) {
        return;
      }

      try {
        setActionLoading(
          "approve",
        );

        await accountUpgradeRequestsApi
          .approve(
            requestId,
            {
  accountType:
    request.requestedAccountType ===
      "وكيل" ||
    request.requestedAccountType ===
      "منصب"
      ? request.requestedAccountType
      : undefined,

  AccountLevel:
    accountLevel ||
    null,

  note:
    approveReason
      .trim() ||
    "تمت الموافقة على طلب ترقية الحساب من لوحة التحكم",
}
          );

        toast.success(
          "تمت الموافقة على طلب الترقية بنجاح",
        );

        await loadRequest();
      } catch (
        error
      ) {
        console.error(
          "Approve account upgrade request failed:",
          error,
        );

        if (
          error instanceof
          ApiError
        ) {
          if (
            error.status ===
            409
          ) {
            toast.error(
              error.message ||
                "تمت معالجة هذا الطلب مسبقًا",
            );

            return;
          }

          if (
            error.status ===
            403
          ) {
            toast.error(
              "لا تمتلك صلاحية الموافقة على الطلب",
            );

            return;
          }

          toast.error(
            error.message ||
              "فشلت الموافقة على الطلب",
          );

          return;
        }

        toast.error(
          "تعذر الاتصال بالخادم",
        );
      } finally {
        setActionLoading(
          null,
        );
      }
    };

  const handleReject =
    async () => {
      if (
        !requestId ||
        !request
      ) {
        return;
      }

      if (
        !rejectReason.trim()
      ) {
        toast.error(
          "يرجى كتابة سبب رفض الطلب",
        );

        return;
      }

      const confirmed =
        window.confirm(
          `هل تريد رفض طلب "${
            request.fullName ||
            request.userId
          }"؟`,
        );

      if (
        !confirmed
      ) {
        return;
      }

      try {
        setActionLoading(
          "reject",
        );

        await accountUpgradeRequestsApi
          .reject(
            requestId,
            {
              reason:
                rejectReason.trim(),
            },
          );

        toast.success(
          "تم رفض طلب الترقية",
        );

        await loadRequest();
      } catch (
        error
      ) {
        console.error(
          "Reject account upgrade request failed:",
          error,
        );

        if (
          error instanceof
          ApiError
        ) {
          if (
            error.status ===
            409
          ) {
            toast.error(
              error.message ||
                "تمت معالجة هذا الطلب مسبقًا",
            );

            return;
          }

          if (
            error.status ===
            403
          ) {
            toast.error(
              "لا تمتلك صلاحية رفض الطلب",
            );

            return;
          }

          toast.error(
            error.message ||
              "فشل رفض الطلب",
          );

          return;
        }

        toast.error(
          "تعذر الاتصال بالخادم",
        );
      } finally {
        setActionLoading(
          null,
        );
      }
    };

  if (
    loading
  ) {
    return (
      <div className="account-upgrade-page">
        <div className="account-upgrade-details-state">
          جاري تحميل تفاصيل
          الطلب...
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="account-upgrade-page">
        <div className="account-upgrade-details-state">
          تعذر العثور على الطلب.
        </div>
      </div>
    );
  }

  return (
    <div className="account-upgrade-page">

      <section className="account-upgrade-details-header">
        <div>
          <span>
            طلب ترقية الحساب
          </span>

          <h1>
            {request.fullName ||
              "مستخدم بدون اسم"}
          </h1>

          <p>
            طلب التحويل إلى{" "}
            <strong>
              {request.requestedAccountType ||
                "غير محدد"}
            </strong>
          </p>
        </div>

        <div className="account-upgrade-details-header-actions">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/dashboard/EditAccount/${request.userId}`,
              )
            }
          >
            <FontAwesomeIcon
              icon={faUser}
            />

            حساب المستخدم
          </button>

          <button
            type="button"
            onClick={() =>
              navigate(
                "/account-upgrade-requests",
              )
            }
          >
            <FontAwesomeIcon
              icon={
                faArrowRight
              }
            />

            رجوع
          </button>
        </div>
      </section>

      <section className="account-upgrade-details-layout">

        <div className="account-upgrade-details-main">

          <div className="account-upgrade-details-card">
            <div className="account-upgrade-details-title">
              <FontAwesomeIcon
                icon={
                  faUserTie
                }
              />

              <div>
                <h2>
                  معلومات الطلب
                </h2>

                <p>
                  البيانات المقدمة من
                  المستخدم.
                </p>
              </div>
            </div>

            <div className="account-upgrade-info-grid">
              <div>
                <span>
                  الاسم الكامل
                </span>

                <strong>
                  {request.fullName ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  رقم الهاتف
                </span>

                <strong dir="ltr">
                  {request.phoneNumber ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  البريد الإلكتروني
                </span>

                <strong dir="ltr">
                  {request.emailAddress ||
                    request.email ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  نوع الحساب المطلوب
                </span>

                <strong>
                  {request.requestedAccountType ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  الحساب الحالي
                </span>

                <strong>
                  {request.currentAccountType ||
                    "مستخدم"}
                </strong>
              </div>

              <div>
                <span>
                  الحالة
                </span>

                <strong>
                  {getStatusLabel(
                    request.status,
                  )}
                </strong>
              </div>

              <div>
                <span>
                  تاريخ تقديم الطلب
                </span>

                <strong>
                  {formatTimestamp(
                    request.createdAt,
                  )}
                </strong>
              </div>

              <div>
                <span>
                  آخر تحديث
                </span>

                <strong>
                  {formatTimestamp(
                    request.updatedAt,
                  )}
                </strong>
              </div>
            </div>
          </div>

          <div className="account-upgrade-details-card">
            <div className="account-upgrade-details-title">
              <FontAwesomeIcon
                icon={
                  faBuilding
                }
              />

              <div>
                <h2>
                  معلومات العمل
                </h2>

                <p>
                  بيانات الوكيل أو
                  الفني المقدمة في
                  الطلب.
                </p>
              </div>
            </div>

            <div className="account-upgrade-info-grid">
              <div>
                <span>
                  اسم الشركة
                </span>

                <strong>
                  {request.companyName ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  سنوات الخبرة
                </span>

                <strong>
                  {request.years ??
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  الخبرة
                </span>

                <strong>
                  {request.expertise ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  المناطق
                </span>

                <strong>
                  {request.regions ||
                    "—"}
                </strong>
              </div>
            </div>

            <div className="account-upgrade-long-field">
              <span>
                الخدمات
              </span>

              <p>
                {request.services ||
                  "لا توجد بيانات"}
              </p>
            </div>

            <div className="account-upgrade-long-field">
              <span>
                الشراكات
              </span>

              <p>
                {request.partnerships ||
                  "لا توجد بيانات"}
              </p>
            </div>

            <div className="account-upgrade-long-field">
              <span>
                العنوان الكامل
              </span>

              <p>
                <FontAwesomeIcon
                  icon={
                    faLocationDot
                  }
                />

                {" "}
                {request.fullAddress ||
                  "لا يوجد عنوان"}
              </p>
            </div>
          </div>

          <div className="account-upgrade-details-card">
            <div className="account-upgrade-details-title">
              <FontAwesomeIcon
                icon={
                  faClockRotateLeft
                }
              />

              <div>
                <h2>
                  سجل تغييرات الحساب
                </h2>

                <p>
                  جميع عمليات تغيير
                  نوع ومستوى الحساب.
                </p>
              </div>
            </div>

            {logsLoading ? (
              <div className="account-upgrade-log-empty">
                جاري تحميل السجل...
              </div>
            ) : logs.length ===
              0 ? (
              <div className="account-upgrade-log-empty">
                لا يوجد سجل تغييرات
                لهذا المستخدم.
              </div>
            ) : (
              <div className="account-upgrade-log-list">
                {logs.map(
                  log => (
                    <div
                      className="account-upgrade-log-card"
                      key={
                        log.logId
                      }
                    >
                      <div className="account-upgrade-log-transition">
                        <div>
                          <span>
                            قبل
                          </span>

                          <strong>
                            {getLogBefore(
                              log,
                            )}

                            {getLogLevelBefore(
                              log,
                            ) !==
                              "—" &&
                              ` / ${getLogLevelBefore(
                                log,
                              )}`}
                          </strong>
                        </div>

                        <span>
                          ←
                        </span>

                        <div>
                          <span>
                            بعد
                          </span>

                          <strong>
                            {getLogAfter(
                              log,
                            )}

                            {getLogLevelAfter(
                              log,
                            ) !==
                              "—" &&
                              ` / ${getLogLevelAfter(
                                log,
                              )}`}
                          </strong>
                        </div>
                      </div>

                      <div className="account-upgrade-log-meta">
                        <span>
                          {log.reason ||
                            "بدون سبب مسجل"}
                        </span>

                        <small>
                          {log.changedByEmail ||
                            log.changedBy ||
                            "النظام"}

                          {" • "}

                          {formatTimestamp(
                            log.createdAt,
                          )}
                        </small>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

        </div>

        <aside className="account-upgrade-details-sidebar">

          <div className="account-upgrade-user-summary">
            <div className="account-upgrade-user-avatar">
              {(request.fullName ||
                "?")
                .trim()
                .charAt(0)
                .toUpperCase()}
            </div>

            <h3>
              {request.fullName ||
                "مستخدم بدون اسم"}
            </h3>

            <p dir="ltr">
              {request.emailAddress ||
                request.email ||
                ""}
            </p>

            <span>
              <FontAwesomeIcon
                icon={faPhone}
              />

              {request.phoneNumber ||
                "لا يوجد هاتف"}
            </span>

            <span>
              <FontAwesomeIcon
                icon={
                  faEnvelope
                }
              />

              {request.emailAddress ||
                request.email ||
                "لا يوجد بريد"}
            </span>

            <span>
              <FontAwesomeIcon
                icon={
                  faShieldHalved
                }
              />

              المطلوب:{" "}
              {request.requestedAccountType ||
                "غير محدد"}
            </span>
          </div>

          {request.status ===
            "pending" && (
            <div className="account-upgrade-review-card">

              <h3>
                مراجعة الطلب
              </h3>

              {availableLevels.length >
                0 && (
                <div className="account-upgrade-review-field">
                  <label>
                    مستوى الحساب
                  </label>

                  <select
                    value={
                      accountLevel
                    }
                    disabled={
                      actionLoading !==
                      null
                    }
                    onChange={
                      event =>
                        setAccountLevel(
                          event
                            .target
                            .value,
                        )
                    }
                  >
                    <option value="">
                      اختر المستوى
                    </option>

                    {availableLevels.map(
                      level => (
                        <option
                          key={
                            level
                          }
                          value={
                            level
                          }
                        >
                          {level}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}

              <div className="account-upgrade-review-field">
                <label>
                  ملاحظة الموافقة
                </label>

                <textarea
                  rows={3}
                  value={
                    approveReason
                  }
                  disabled={
                    actionLoading !==
                    null
                  }
                  onChange={
                    event =>
                      setApproveReason(
                        event
                          .target
                          .value,
                      )
                  }
                  placeholder="اختياري"
                />
              </div>

              <button
                type="button"
                className="account-upgrade-approve-btn"
                disabled={
                  actionLoading !==
                  null
                }
                onClick={
                  handleApprove
                }
              >
                <FontAwesomeIcon
                  icon={
                    faCheck
                  }
                />

                {actionLoading ===
                "approve"
                  ? "جاري الموافقة..."
                  : "الموافقة على الطلب"}
              </button>

              <div className="account-upgrade-review-separator">
                أو
              </div>

              <div className="account-upgrade-review-field">
                <label>
                  سبب الرفض
                </label>

                <textarea
                  rows={4}
                  value={
                    rejectReason
                  }
                  disabled={
                    actionLoading !==
                    null
                  }
                  onChange={
                    event =>
                      setRejectReason(
                        event
                          .target
                          .value,
                      )
                  }
                  placeholder="سبب الرفض مطلوب"
                />
              </div>

              <button
                type="button"
                className="account-upgrade-reject-btn"
                disabled={
                  actionLoading !==
                  null
                }
                onClick={
                  handleReject
                }
              >
                <FontAwesomeIcon
                  icon={
                    faXmark
                  }
                />

                {actionLoading ===
                "reject"
                  ? "جاري الرفض..."
                  : "رفض الطلب"}
              </button>
            </div>
          )}

          {request.status !==
            "pending" && (
            <div className="account-upgrade-processed-card">
              <span>
                حالة الطلب
              </span>

              <strong>
                {getStatusLabel(
                  request.status,
                )}
              </strong>

              {(request.rejectionReason ||
                request.reason) && (
                <p>
                  {request.rejectionReason ||
                    request.reason}
                </p>
              )}

              <small>
                {request.reviewedByEmail ||
                  request.approvedByEmail ||
                  request.rejectedByEmail ||
                  ""}
              </small>
            </div>
          )}

        </aside>
      </section>
    </div>
  );
};

export default AccountUpgradeRequestDetails;